

import React, { useState, useEffect } from 'react';
import {
  Card,
  IconButton,
  Chip,
  Tooltip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { KeyboardArrowRight, Lock, InfoOutlined } from '@mui/icons-material';
import MDDialog from 'components/MDDialog';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'auth/Auth';
import { useTheme } from '@mui/material/styles';
import { v2 as t, v2Tones } from 'styles/tokens/v2';

function SettingsCard({ 
  title, 
  icon, 
  description, 
  component, 
  comingSoon, 
  isNavigate, 
  navigateTo,
  disabled = false,
  playgroundRestricted = false,
  // Set when the tenant has not bought the module this card configures. The
  // card stays visible -- an admin needs to see that the feature exists in
  // order to ask for it -- but it does not open.
  lockedReason = null,
  // v2 settings search: areas inside this card that match the query, as
  // { label, onClick }. Each opens that area (or the card) on its own.
  matches = []
}) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [integrationData, setIntegrationData] = useState(null);
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  const isV2 = useTheme()?.pearlUi === 'v2';

  // Determine if card should be disabled
  const isCardDisabled = disabled || playgroundRestricted || comingSoon || Boolean(lockedReason);

  const handleOpen = () => {
    console.log('SettingsCard handleOpen called:', { title, isNavigate, navigateTo, comingSoon, playgroundRestricted });
    
    // Don't open if disabled, restricted, or coming soon
    if (isCardDisabled) {
      console.log(`Card ${title} is disabled/restricted`);
      return;
    }
    
    if (isNavigate && navigateTo) {
      console.log('Navigating to:', navigateTo);
      navigate(navigateTo);
      return;
    }
    console.log('Opening dialog');
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  // Fetch company info when Integrations dialog opens
  useEffect(() => {
    if (open && title === 'Integrations') {
      const fetchCompanyInfo = async () => {
        try {
          const response = await axiosInstance.get('/company-api-keys/info');
          setCompanyInfo(response.data);
        } catch (error) {
          console.error("Error fetching company info:", error);
        }
      };
      fetchCompanyInfo();
    }
  }, [open, title, axiosInstance]);

  const handleSave = async () => {
    if (!companyInfo || !integrationData) {
      return;
    }
  
    setIsSaving(true);
    try {
      const payload = {
        company_name: companyInfo.company_name,
        maps_api_key: integrationData.googleMapsApiKey,
        ai_api_key: integrationData.aiApiKey,
        gst_api_key: integrationData.gstApiKey,
        webhooks: integrationData.webhooks
      };
      
      await axiosInstance.post('/company-api-keys/', payload);
      setOpen(false); // Close dialog after successful save
    } catch (error) {
      console.error("Error saving API keys:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDataChange = (data) => {
    setIntegrationData(data);
  };

  // v2 tile (design-reference "Settings · Overview"): icon and title on one
  // line, the description under them; the whole card opens the section.
  const v2Card = (
    <Card
      onClick={handleOpen}
      role={isCardDisabled ? undefined : 'button'}
      tabIndex={isCardDisabled ? -1 : 0}
      aria-disabled={isCardDisabled || undefined}
      onKeyDown={(e) => {
        if (!isCardDisabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.currentTarget.click(); }
      }}
      sx={{
        width: '100%',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        cursor: isCardDisabled ? 'not-allowed' : 'pointer',
        backgroundColor: isCardDisabled ? t.surface2 : t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: '10px',
        boxShadow: 'none',
        transition: 'border-color 120ms, box-shadow 120ms',
        '&:hover': isCardDisabled ? {} : { borderColor: t.border2, boxShadow: t.shadow },
        '&:focus-visible': { outline: `2px solid ${t.primary}`, outlineOffset: 2 },
      }}
    >
      <MDBox display="flex" alignItems="center" gap={1.5}>
        <MDBox
          sx={{
            width: 40, height: 40, flexShrink: 0, borderRadius: '8px',
            display: 'grid', placeItems: 'center',
            bgcolor: isCardDisabled ? t.hover : v2Tones.info.bg,
            color: isCardDisabled ? t.text3 : t.primary,
            '& svg': { fontSize: '22px !important', color: 'inherit' },
            '& img': { width: '26px !important', height: '26px !important', padding: '0 !important', boxShadow: 'none !important', background: 'transparent !important' },
          }}
        >
          {icon}
        </MDBox>
        <MDTypography component="h2" sx={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 600, lineHeight: 1.3, color: isCardDisabled ? t.text3 : t.text }}>
          {title}
        </MDTypography>
        {(playgroundRestricted || lockedReason) && (
          <Tooltip title={lockedReason || 'Locked in playground mode'}>
            <Lock sx={{ fontSize: '18px !important', color: v2Tones.warning.fg }} />
          </Tooltip>
        )}
        {comingSoon && (
          <Chip label="Coming soon" size="small" sx={{ height: 22, fontSize: 12, fontWeight: 600, bgcolor: v2Tones.neutral.bg, color: v2Tones.neutral.fg }} />
        )}
      </MDBox>
      <MDTypography sx={{ fontSize: 14, lineHeight: 1.5, color: t.text2 }}>
        {description}
      </MDTypography>
      {(playgroundRestricted || lockedReason) && (
        <MDBox component="span" sx={{ alignSelf: 'flex-start', px: 1, py: 0.25, borderRadius: '6px', fontSize: 12, fontWeight: 600, bgcolor: v2Tones.warning.bg, color: v2Tones.warning.fg }}>
          {playgroundRestricted ? 'Not available in playground mode' : lockedReason}
        </MDBox>
      )}
      {matches.length > 0 && (
        <MDBox component="ul" aria-label={`Matching areas in ${title}`} sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {matches.map((m) => (
            <li key={m.label}>
              <Chip
                label={m.label}
                size="small"
                clickable
                onClick={(e) => {
                  e.stopPropagation();
                  m.onClick();
                }}
                sx={{ height: 26, fontSize: 12.5, fontWeight: 600, bgcolor: v2Tones.info.bg, color: v2Tones.info.fg, '&:hover': { bgcolor: '#D6E6FA' } }}
              />
            </li>
          ))}
        </MDBox>
      )}
    </Card>
  );

  return (
    <>
      {isV2 ? v2Card : (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          minHeight: '280px',
          cursor: isCardDisabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          opacity: isCardDisabled ? 0.6 : 1,
          backgroundColor: isCardDisabled ? '#f5f5f5' : 'white',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            transform: isCardDisabled ? 'none' : 'translateY(-4px)',
            boxShadow: isCardDisabled ? 'none' : '0 4px 12px rgba(0,0,0,0.1)'
          }
        }}
        onClick={handleOpen}
      >
        <MDBox p={3} display="flex" flexDirection="column" height="100%">
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="4rem"
              height="4rem"
              borderRadius="lg"
              color="white"
              bgColor={isCardDisabled ? "grey" : "info"}
              variant={isCardDisabled ? "contained" : "gradient"}
              sx={{ opacity: isCardDisabled ? 0.7 : 1 }}
            >
              {icon}
            </MDBox>
            <MDBox display="flex" alignItems="center" gap={1}>
              {playgroundRestricted && (
                <Tooltip title="Not available during playground mode" placement="top">
                  <InfoOutlined 
                    sx={{ 
                      color: '#ff9800',
                      fontSize: '1.2rem'
                    }} 
                  />
                </Tooltip>
              )}
              {comingSoon && (
                <Chip
                  label="Coming Soon"
                  color="default"
                  size="small"
                  sx={{ opacity: 0.7 }}
                />
              )}
            </MDBox>
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={1}>
            <MDTypography 
              variant="h5" 
              fontWeight="medium"
              sx={{ color: isCardDisabled ? '#757575' : 'inherit' }}
            >
              {title}
            </MDTypography>
            {(playgroundRestricted || lockedReason) && (
              <Tooltip title={lockedReason || "Locked in playground mode"}>
                <Lock 
                  sx={{ 
                    fontSize: '1.1rem',
                    color: '#ff9800'
                  }} 
                />
              </Tooltip>
            )}
          </MDBox>
          <MDBox 
            mb={2} 
            sx={{ 
              flex: '1 1 auto',
              minHeight: '60px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start'
            }}
          >
            <MDTypography 
              variant="body2" 
              color="text" 
              sx={{ 
                opacity: isCardDisabled ? 0.6 : 1,
                flex: '1 1 auto'
              }}
            >
              {description}
            </MDTypography>
            
            {/* Show playground restricted badge */}
            {playgroundRestricted && (
              <MDBox mt={1}>
                <MDTypography 
                  variant="caption" 
                  sx={{ 
                    color: '#ff9800',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  🔒 Playground Restricted
                </MDTypography>
              </MDBox>
            )}

            {lockedReason && !playgroundRestricted && (
              <MDBox mt={1}>
                <MDTypography 
                  variant="caption" 
                  sx={{ 
                    color: '#ff9800',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  🔒 {lockedReason}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
          
          {!isCardDisabled && (
            <MDBox mt="auto" display="flex" justifyContent="flex-end" pt={2}>
              <IconButton size="small" color="info">
                <KeyboardArrowRight />
              </IconButton>
            </MDBox>
          )}
        </MDBox>
      </Card>
      )}

        {!isNavigate && component && !isCardDisabled && (
        <MDDialog
          open={open}
          onClose={handleClose}
          title={title}
          icon={icon}
          actions={title === 'Integrations' ? (
            <>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleSave}
                disabled={isSaving || !companyInfo}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </MDButton>
              <MDButton onClick={handleClose} color="secondary">
                Close
              </MDButton>
            </>
          ) : undefined}
        >
          {(() => {
            // Handle both: function-based and element-based components
            const rendered =
              typeof component === "function"
                ? component()
                : component;

            return title === "Integrations"
              ? React.cloneElement(rendered, {
                  onSave: handleSave,
                  isSaving,
                  companyInfo,
                  onDataChange: handleDataChange,
                })
              : rendered;
          })()}
        </MDDialog>
      )}

    </>
  );
}

export default SettingsCard;